import React, { memo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import BootstrapModalLayout from '../../ui/BootstrapModalLayout'
import { ChargePoint, useCompanyGetChargePointsPagedQuery } from '../../common/redux/api/web-api'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../lib/table'
import { ChargepointStatus } from '../../lib/chargepoint-status'
import Text from '../../ui/Text'
import localizeDate from '../../common/utils/localizeDate'
import SettingsInfo from '../../ui/chargepoints/SettingsInfo'
import { Button } from '../../common/components/Button'
import useKeyboardHandle from '../../common/hooks/useKeyboardHandle'
import { ChargersPagination } from '../../lib/chargers-pagination'
import { RootState } from '../redux/store'
import { filterFieldsBySearchValue } from '../../lib/filter-fields-by-search-value'
import Input from '../../common/components/Input'
import useText from '../../common/hooks/useText'

export type AddChargePointToPoolModalProps = {
    onAdd: (chargePoint: ChargePoint) => void
    onCancel: () => void
}

export const AddChargePointToPoolModal = memo(function AddEditPoolModal({
    onAdd,
    onCancel,
}: AddChargePointToPoolModalProps): React.ReactElement {
    const text = useText()
    useKeyboardHandle({ onEscapePress: onCancel })
    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)

    const { data: chargePoints, isLoading: isLoadingChargePoints } = useCompanyGetChargePointsPagedQuery(
        {
            companyId,
        },
        { refetchOnMountOrArgChange: true }
    )

    const [filteredChargePoints, setFilteredChargePoints] = useState<ChargePoint[]>([])
    const [searchValue, setSearchValue] = useState('')
    const [currentPage, setCurrentPage] = useState(0)
    const [pageSize] = useState(10)
    const offset = currentPage * pageSize

    useEffect(() => {
        setFilteredChargePoints(() => {
            return chargePoints
                ? chargePoints?.items?.filter(
                      (cp) => cp.poolId === null && filterFieldsBySearchValue([cp.id, cp.name], searchValue)
                  )
                : []
        })
    }, [chargePoints, offset, pageSize, searchValue])

    return (
        <BootstrapModalLayout
            title={text('lblAddChargePoint')}
            size={'lg'}
            actions={[
                {
                    label: text('btnCancel'),
                    btn: 'btn-secondary',
                    callback: onCancel,
                },
            ]}
        >
            <div className="row margin-bottom-small">
                <div className="w-12 md:w-4">
                    <Input
                        data-testid="search"
                        type="text"
                        name="search"
                        id="search"
                        labelText={text('msgSearch')}
                        value={searchValue}
                        onChange={(event) => {
                            setCurrentPage(0)
                            setSearchValue(event.target.value)
                        }}
                    />
                </div>
            </div>
            {isLoadingChargePoints ? (
                <div className="shimmer-container shimmer-small" />
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>
                                <Text code="lblSerialNumberShort" />
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <Text code="lblStatus" />
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <Text code="lblSettings" />
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <Text code="lblLastSeenTime" />
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <Text code="lblName" />
                            </TableHeaderCell>
                            <TableHeaderCell />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filteredChargePoints &&
                            filteredChargePoints?.slice(offset, offset + pageSize).map((chargePoint, index) => (
                                <TableRow key={index}>
                                    <TableBodyCell>{chargePoint.id}</TableBodyCell>
                                    <TableBodyCell>
                                        <ChargepointStatus chargePoint={chargePoint || ''} />
                                    </TableBodyCell>
                                    <TableBodyCell>
                                        <SettingsInfo chargePoint={chargePoint || ''} />
                                    </TableBodyCell>
                                    <TableBodyCell>
                                        {(chargePoint?.chargePointStatus === 'Offline' &&
                                            localizeDate(chargePoint?.lastHeartbeat || '')) ||
                                            '---'}
                                    </TableBodyCell>
                                    <TableBodyCell className="hide-overflow">{chargePoint?.name || ''}</TableBodyCell>
                                    <TableBodyCell>
                                        <Button
                                            data-testid={`add-button-charger-${chargePoint.id}`}
                                            size="xs"
                                            onClick={() => {
                                                onAdd(chargePoint)
                                            }}
                                        >
                                            {text('btnAdd')}
                                        </Button>
                                    </TableBodyCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            )}
            {filteredChargePoints.length > 0 && (
                <ChargersPagination
                    totalCount={filteredChargePoints?.length}
                    pageSize={pageSize}
                    pageIndex={currentPage}
                    displayedChargers={pageSize}
                    setPageIndex={(index) => {
                        setCurrentPage(index)
                    }}
                />
            )}
        </BootstrapModalLayout>
    )
})
